import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Printer, Edit, Coins, X, XCircle, Trash, FileText, Paperclip, Send, Check, CheckCircle, Clock, Building } from 'lucide-react';
// import { useReactToPrint } from 'react-to-print'; // Removed
import { message, Spin, Input, DatePicker, Card, Descriptions, Table, Tag, Divider, Button, Modal, Row, Col, Select, Timeline } from 'antd';
import {
    ClockCircleOutlined,
    EditOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    StopOutlined,
    DollarOutlined,
    PlusCircleOutlined,
    BankOutlined,
    UserOutlined,
    EnvironmentOutlined,
    BarsOutlined,
    HistoryOutlined,
    ThunderboltOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    CheckCircleFilled,
    CloseCircleOutlined,
    SendOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import {
    fetchInvoicesWithEditStatus,
    fetchInvoiceById, // Import the new single-invoice fetch function
    fetchConfig,
    fetchClients,
    fetchUsers,
    updateInvoice,
    addInvoicePayment,
    updateInvoicePaymentItem, // Import new API functions
    deleteInvoicePaymentItem, // Import new API functions
    downloadInvoicePDF,
    verifyPasscode,
    cancelInvoice,
    deleteInvoice,
    logInvoiceEvent,
    submitInvoiceForReview,
    verifyInvoice,
    approveInvoice,
    rejectInvoice,
    assignInvoice
} from '../utils/api';


import { downloadGSTJSON } from '../utils/jsonGenerator';
import { isViewer, isAdmin, getStoredUser } from '../utils/auth';
import { formatInvoiceNumber } from '../utils/invoiceNumber';
import InvoicePreview from '../components/InvoicePreview';
import AdhocInvoicePreview from '../components/AdhocInvoicePreview';
import EditInvoiceModal from '../components/EditInvoiceModal';
import { TDS_SECTIONS, findTdsSection, formatTdsSectionLabel } from '../utils/tdsSections';
import './InvoiceDetails.css';

// Helper to format currency
const formatIndianCurrency = (num) => {
    const n = Number(num);
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
    }).format(n);
};

function InvoiceDetails({ currentUser }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [config, setConfig] = useState({});
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    // Workflow state
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectComment, setRejectComment] = useState('');
    const [submitModalOpen, setSubmitModalOpen] = useState(false);
    const [submitComment, setSubmitComment] = useState('');
    const [submitAssignee, setSubmitAssignee] = useState(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedAssignee, setSelectedAssignee] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Modal states
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        receivedAmount: '',
        utrNumber: '',
        receivedDate: dayjs().format('YYYY-MM-DD'),
        paymentMode: 'Bank Transfer',
        paymentNotes: ''
    });
    const [editingPayment, setEditingPayment] = useState(null); // Track which payment is being edited

    const [editingInvoice, setEditingInvoice] = useState(null);
    const printRef = useRef();

    const activeUser = currentUser || getStoredUser();
    const userIsAdmin = activeUser?.role === 'admin';
    const userIsViewer = activeUser?.role === 'viewer';
    const creatorId = invoice?.creator?._id || invoice?.creator?.id || invoice?.creator;
    const currentUserId = activeUser?._id || activeUser?.id;
    const isCreator = creatorId && currentUserId && String(creatorId) === String(currentUserId);

    const loadData = async () => {
        try {
            setLoading(true);
            const [invoiceData, configData, clientsData, usersData] = await Promise.all([
                fetchInvoiceById(id),
                fetchConfig(),
                fetchClients(),
                fetchUsers().catch(() => [])
            ]);

            setInvoice(invoiceData);
            setConfig(configData || {});
            setClients(clientsData || []);
            setUsers(Array.isArray(usersData) ? usersData : []);
        } catch (error) {
            console.error('Error loading invoice details:', error);
            message.error('Failed to load invoice details');
        } finally {
            setLoading(false);
        }
    };

    // Workflow actions
    const handleSubmitForReview = async () => {
        try {
            setActionLoading(true);
            await submitInvoiceForReview(invoice._id || invoice.id, {
                comment: submitComment.trim() || undefined,
                assignToUserId: submitAssignee || undefined
            });
            message.success('Invoice submitted for review successfully');
            setSubmitModalOpen(false);
            setSubmitComment('');
            setSubmitAssignee(null);
            await loadData();
        } catch (error) {
            console.error('Error submitting invoice for review:', error);
            message.error(error.message || 'Failed to submit invoice for review');
        } finally {
            setActionLoading(false);
        }
    };

    const handleVerifyInvoice = () => {
        Modal.confirm({
            title: 'Verify Invoice',
            content: 'Are you sure you want to verify this invoice? This confirms the invoice has been checked and is ready for final admin approval.',
            okText: 'Verify',
            okType: 'primary',
            onOk: async () => {
                try {
                    setActionLoading(true);
                    await verifyInvoice(invoice._id || invoice.id, { comment: 'Verified by reviewer' });
                    message.success('Invoice verified successfully');
                    await loadData();
                } catch (error) {
                    console.error('Error verifying invoice:', error);
                    message.error(error.message || 'Failed to verify invoice');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const handleApproveInvoice = () => {
        Modal.confirm({
            title: 'Approve Invoice',
            content: 'Are you sure you want to approve this invoice? Once approved, payments can be recorded.',
            okText: 'Approve',
            okButtonProps: { style: { background: '#52c41a', borderColor: '#52c41a' } },
            onOk: async () => {
                try {
                    setActionLoading(true);
                    await approveInvoice(invoice._id || invoice.id, { comment: 'Approved by admin' });
                    message.success('Invoice approved successfully');
                    await loadData();
                } catch (error) {
                    console.error('Error approving invoice:', error);
                    message.error(error.message || 'Failed to approve invoice');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const handleRejectInvoice = async () => {
        if (!rejectComment.trim()) {
            message.error('Please enter a rejection reason');
            return;
        }
        try {
            setActionLoading(true);
            await rejectInvoice(invoice._id || invoice.id, { comment: rejectComment.trim() });
            message.success('Invoice rejected and returned for rework');
            setRejectModalOpen(false);
            setRejectComment('');
            await loadData();
        } catch (error) {
            console.error('Error rejecting invoice:', error);
            message.error(error.message || 'Failed to reject invoice');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAssignInvoice = async () => {
        if (!selectedAssignee) {
            message.error('Please select a user to assign');
            return;
        }
        try {
            setActionLoading(true);
            await assignInvoice(invoice._id || invoice.id, selectedAssignee);
            message.success('Invoice assigned successfully');
            setAssignModalOpen(false);
            setSelectedAssignee(null);
            await loadData();
        } catch (error) {
            console.error('Error assigning invoice:', error);
            message.error(error.message || 'Failed to assign invoice');
        } finally {
            setActionLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const handlePrint = async () => {
        try {
            message.loading({ content: 'Preparing print...', key: 'print' });
            const blob = await downloadInvoicePDF(invoice._id || invoice.id);
            const url = window.URL.createObjectURL(blob);

            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            iframe.src = url;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                message.success({ content: 'Ready to print', key: 'print' });
            };
        } catch (error) {
            console.error('Print Error:', error);
            message.error({ content: 'Failed to load invoice PDF', key: 'print' });
        }
    };



    const handleDownloadPDF = async () => {
        if (downloading) return;
        try {
            setDownloading(true);
            const blob = await downloadInvoicePDF(invoice._id || invoice.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const clientName = (invoice.clientName || invoice.client?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
            const filename = invoice.invoiceType === 'credit_note'
                ? `CreditNote_${invoice.creditNoteNumber || invoice.invoiceNumber}_${clientName}.pdf`
                : `Invoice_${invoice.invoiceNumber}_${clientName}.pdf`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('PDF Download Error:', error);
            message.error('Failed to download PDF');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadJSON = async () => {
        try {
            await downloadGSTJSON(invoice, config);
            message.success('GST JSON export initiated successfully.');
            // Log the event
            await logInvoiceEvent(invoice._id || invoice.id, 'JSON_DOWNLOADED', 'GST JSON exported');
            // Refresh invoice data to show new timeline event
            loadData();
        } catch (error) {
            console.error('JSON Download Error:', error);
            message.error(error.message || 'Failed to download JSON file.');
        }
    };

    // Helper to get timeline icon
    const getTimelineIcon = (type) => {
        switch (type) {
            case 'CREATED': return <PlusCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />;
            case 'EDITED': return <EditOutlined style={{ fontSize: '16px', color: '#1890ff' }} />;
            case 'PDF_DOWNLOADED': return <FilePdfOutlined style={{ fontSize: '16px', color: '#ff4d4f' }} />;
            case 'JSON_DOWNLOADED': return <FileTextOutlined style={{ fontSize: '16px', color: '#faad14' }} />;
            case 'CANCELLED': return <StopOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />;
            case 'PAYMENT_ADDED': return <DollarOutlined style={{ fontSize: '16px', color: '#52c41a' }} />;
            case 'SUBMITTED_FOR_REVIEW': return <SendOutlined style={{ fontSize: '16px', color: '#1890ff' }} />;
            case 'VERIFIED': return <CheckCircleOutlined style={{ fontSize: '16px', color: '#13c2c2' }} />;
            case 'APPROVED': return <CheckCircleFilled style={{ fontSize: '16px', color: '#52c41a' }} />;
            case 'REJECTED': return <CloseCircleOutlined style={{ fontSize: '16px', color: '#ff4d4f' }} />;
            case 'ASSIGNED': return <UserOutlined style={{ fontSize: '16px', color: '#722ed1' }} />;
            default: return <ClockCircleOutlined style={{ fontSize: '16px' }} />;
        }
    };

    const getTimelineText = (item) => {
        const dateStr = dayjs(item.date).format('DD MMM YYYY, h:mm A');
        const userName = item.userId?.name || item.userId?.email || 'System';

        let actionText = '';
        switch (item.type) {
            case 'CREATED': actionText = 'Created by'; break;
            case 'EDITED': actionText = 'Edited by'; break;
            case 'PDF_DOWNLOADED': actionText = 'PDF Downloaded by'; break;
            case 'JSON_DOWNLOADED': actionText = 'JSON Downloaded by'; break;
            case 'CANCELLED': actionText = 'Cancelled by'; break;
            case 'PAYMENT_ADDED': actionText = 'Payment entered by'; break;
            case 'SUBMITTED_FOR_REVIEW': actionText = 'Submitted for review by'; break;
            case 'VERIFIED': actionText = 'Verified by'; break;
            case 'APPROVED': actionText = 'Approved by'; break;
            case 'REJECTED': actionText = 'Rejected by'; break;
            case 'ASSIGNED': actionText = 'Assigned by'; break;
            default: actionText = 'Updated by';
        }

        return (
            <div>
                <div style={{ fontWeight: 500 }}>{actionText} {userName}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{dateStr}</div>
                {item.details && <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{item.details}</div>}
            </div>
        );
    };

    // Sort timeline: Oldest first or Newest first?
    // Sort timeline: Oldest first or Newest first?
    const sortedTimeline = invoice ? [...(invoice.timeline || [])].sort((a, b) => new Date(b.date) - new Date(a.date)) : [];

    // If no timeline exists (legacy invoices), show at least creation
    if (invoice && sortedTimeline.length === 0 && invoice.createdAt) {
        sortedTimeline.push({
            type: 'CREATED',
            date: invoice.createdAt,
            userId: invoice.creator,
            details: 'Invoice created'
        });
    }

    // Cancel Invoice Handler
    const handleCancelInvoice = () => {
        let passcodeValue = '';
        Modal.confirm({
            title: 'Cancel Invoice',
            content: (
                <div>
                    <p style={{ marginBottom: 16 }}>Are you sure you want to cancel this invoice? This will exclude it from all exports and calculations.</p>
                    <Input.Password
                        placeholder="Enter passcode"
                        onChange={(e) => { passcodeValue = e.target.value; }}
                    />
                </div>
            ),
            okText: 'Cancel Invoice',
            okType: 'danger',
            cancelText: 'Go Back',
            onOk: async () => {
                if (!passcodeValue) {
                    message.error('Please enter passcode');
                    return Promise.reject();
                }
                try {
                    const result = await verifyPasscode(passcodeValue);
                    if (!result.valid) {
                        message.error('Incorrect passcode. Invoice not cancelled.');
                        return Promise.reject();
                    }
                    await cancelInvoice(invoice._id || invoice.id);
                    message.success('Invoice cancelled successfully');
                    setInvoice(prev => ({ ...prev, isCancelled: true }));
                } catch (err) {
                    message.error('Failed to cancel invoice. Please try again.');
                    return Promise.reject();
                }
            }
        });
    };

    // Delete Invoice Handler
    const handleDeleteInvoice = () => {
        let passcodeValue = '';
        Modal.confirm({
            title: 'Delete Invoice',
            content: (
                <div>
                    <p style={{ marginBottom: 16, color: '#ff4d4f' }}>Are you sure you want to permanently delete this invoice? This action cannot be undone.</p>

                    <Input.Password
                        placeholder="Enter passcode"
                        onChange={(e) => { passcodeValue = e.target.value; }}
                    />
                </div>
            ),
            okText: 'Delete Invoice',
            okType: 'danger',
            cancelText: 'Go Back',
            onOk: async () => {
                if (!passcodeValue) {
                    message.error('Please enter passcode');
                    return Promise.reject();
                }
                try {
                    const result = await verifyPasscode(passcodeValue);
                    if (!result.valid) {
                        message.error('Incorrect passcode. Invoice not deleted.');
                        return Promise.reject();
                    }
                    await deleteInvoice(invoice._id || invoice.id);
                    message.success('Invoice deleted successfully');
                    navigate('/invoice-history');
                    setTimeout(() => {
                        window.location.reload();
                    }, 500);
                } catch (err) {
                    message.error('Failed to delete invoice. Please try again.');
                    return Promise.reject();
                }
            }
        });
    };

    // Payment Handlers
    const openPaymentModal = (payment = null) => {
        // Guard against event object being passed when called from onClick={openPaymentModal}
        const isRealPayment = payment && (payment._id || payment.id || payment.amount !== undefined);
        const subtotal = Number(invoice?.totals?.subtotal || 0);
        const invTotal = Number(invoice?.totals?.total || 0);

        // Calculate settled amounts before this payment
        const allPayments = invoice?.payments || [];
        const previousPayments = isRealPayment
            ? allPayments.filter(p => String(p._id || p.id) !== String(payment._id || payment.id))
            : allPayments;
        const priorReceived = previousPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
        const priorTds = previousPayments.reduce((s, p) => s + (Number(p.tdsAmount) || 0), 0);
        const grossRemaining = Math.max(0, invTotal - priorReceived - priorTds);

        if (isRealPayment) {
            console.log('[DEBUG] Opening payment modal for EDIT. record:', payment);
            setEditingPayment(payment);
            const currentTdsAmount = Number(payment.tdsAmount || 0);
            const currentTdsPercent = subtotal > 0 && currentTdsAmount > 0
                ? Math.round((currentTdsAmount / subtotal) * 10000) / 100
                : (invoice.tdsPercent !== undefined && invoice.tdsPercent !== null ? invoice.tdsPercent : '');
            setPaymentForm({
                receivedAmount: String(payment.amount),
                tdsPercent: currentTdsPercent !== '' ? String(currentTdsPercent) : '',
                tdsAmount: currentTdsAmount > 0 ? String(currentTdsAmount) : '',
                tdsSection: payment.tdsSection || (invoice.tdsSection || (invoice.invoiceType === 'rent' ? '194-I' : (invoice.invoiceType === 'adhoc' ? '194C' : ''))),
                utrNumber: payment.utrNumber || '',
                receivedDate: dayjs(payment.date).format('YYYY-MM-DD'),
                paymentMode: payment.paymentMode || 'Bank Transfer',
                paymentNotes: payment.notes || ''
            });
        } else {
            console.log('[DEBUG] Opening payment modal for NEW payment');
            setEditingPayment(null);
            // Suggest TDS section based on invoice type: rent standard is 10% (194-I), adhoc is suggested at 2% (194C)
            const defaultSection = invoice.invoiceType === 'rent' ? '194-I' : (invoice.tdsSection || '194C');

            setPaymentForm({
                receivedAmount: '', // Do NOT auto-fill received cash - let user enter the actual bank deposit
                tdsPercent: '',     // Let user enter the TDS percentage
                tdsAmount: '',      // Auto-calculated from TDS percentage
                tdsSection: defaultSection,
                utrNumber: '',
                receivedDate: dayjs().format('YYYY-MM-DD'),
                paymentMode: 'Bank Transfer',
                paymentNotes: ''
            });
        }
        setPaymentModalOpen(true);
    };

    const handleTdsRateChange = (rateVal, newSection = null) => {
        const subtotal = Number(invoice?.totals?.subtotal || 0);

        const rate = parseFloat(rateVal);
        if (isNaN(rate) || rate <= 0) {
            setPaymentForm(p => ({
                ...p,
                tdsPercent: rateVal === '' ? '' : '0',
                tdsAmount: '0',
                tdsSection: newSection !== null ? newSection : (rateVal === 0 ? '' : p.tdsSection)
                // Do NOT touch receivedAmount
            }));
            return;
        }

        // Auto-calculate TDS amount from TDS percentage applied to base subtotal
        const calculatedTds = Math.round(subtotal * (rate / 100) * 100) / 100;
        setPaymentForm(p => ({
            ...p,
            tdsPercent: String(rateVal),
            tdsAmount: String(calculatedTds),
            tdsSection: newSection !== null ? newSection : p.tdsSection
            // Do NOT touch receivedAmount
        }));
    };

    const handleTdsSectionChange = (secCode) => {
        if (!secCode) {
            setPaymentForm(p => ({ ...p, tdsSection: '' }));
            return;
        }
        const found = findTdsSection(secCode);
        if (found && found.defaultRate != null) {
            handleTdsRateChange(found.defaultRate, secCode);
        } else {
            setPaymentForm(p => ({ ...p, tdsSection: secCode }));
        }
    };

    const handleTdsAmountChange = (amountVal) => {
        const subtotal = Number(invoice?.totals?.subtotal || 0);

        const tdsAmt = parseFloat(amountVal);
        if (isNaN(tdsAmt) || tdsAmt <= 0) {
            setPaymentForm(p => ({
                ...p,
                tdsAmount: amountVal === '' ? '' : '0',
                tdsPercent: '0'
                // Do NOT touch receivedAmount
            }));
            return;
        }

        // Auto-calculate TDS percent from TDS amount applied to base subtotal
        const derivedRate = subtotal > 0 ? Math.round((tdsAmt / subtotal) * 10000) / 100 : 0;
        setPaymentForm(p => ({
            ...p,
            tdsAmount: String(amountVal),
            tdsPercent: String(derivedRate)
            // Do NOT touch receivedAmount
        }));
    };

    const handleSavePayment = async () => {
        try {
            const receivedNum = Number(paymentForm.receivedAmount);
            if (isNaN(receivedNum) || receivedNum <= 0) {
                message.error('Please enter a valid received amount greater than 0.');
                return;
            }

            const payload = {
                amount: receivedNum,
                tdsAmount: Number(paymentForm.tdsAmount || 0),
                tdsPercent: paymentForm.tdsPercent !== '' ? Number(paymentForm.tdsPercent) : undefined,
                tdsSection: paymentForm.tdsSection || undefined,
                date: paymentForm.receivedDate,
                paymentMode: paymentForm.paymentMode,
                utrNumber: paymentForm.utrNumber,
                notes: paymentForm.paymentNotes
            };

            let updatedInv;
            if (editingPayment) {
                // Defensive ID extraction
                const paymentId = editingPayment._id || editingPayment.id || (editingPayment.utrNumber ? invoice.payments?.find(p => p.utrNumber === editingPayment.utrNumber)?._id : null);

                console.log('[DEBUG] Saving payment update:', {
                    paymentId,
                    editingPaymentId: editingPayment._id,
                    editingPaymentIdAlt: editingPayment.id,
                    payload
                });

                if (!paymentId) {
                    throw new Error('This payment record is missing a valid ID.');
                }

                updatedInv = await updateInvoicePaymentItem(invoice._id || invoice.id, paymentId, payload);
                message.success('Payment details updated successfully!');
            } else {
                updatedInv = await addInvoicePayment(invoice._id || invoice.id, payload);
                message.success(`Payment of ${formatIndianCurrency(receivedNum)} added successfully!`);
            }
            setPaymentModalOpen(false);
            setEditingPayment(null);
            setInvoice(updatedInv);
        } catch (err) {
            console.error('Payment save error:', err);
            message.error('Failed to save payment details: ' + err.message);
        }
    };

    const handleDeletePayment = (pId) => {
        // Handle both string IDs and payment objects
        const paymentId = typeof pId === 'string' ? pId : (pId?._id || pId?.id);

        console.log('[DEBUG] Attempting to delete payment:', paymentId);

        if (!paymentId) {
            message.error('Cannot delete: Payment ID is missing.');
            return;
        }
        Modal.confirm({
            title: 'Delete Payment',
            content: 'Are you sure you want to delete this payment record?',
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteInvoicePaymentItem(invoice._id || invoice.id, paymentId);
                    message.success('Payment deleted successfully');
                    loadData();
                } catch (err) {
                    message.error('Failed to delete payment: ' + err.message);
                }
            }
        });
    };

    // Edit Handlers
    const openEditModal = () => {
        setEditingInvoice(invoice);
    };

    const handleEditSubmit = async (updatedInvoiceData) => {
        try {
            const result = await updateInvoice(editingInvoice._id, updatedInvoiceData);
            setInvoice(result);
            setEditingInvoice(null);
            message.success('Invoice updated successfully!');
            
            // Refresh the app after update
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.error('Error updating invoice:', error);
            message.error('Failed to update invoice');
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!invoice) return <div>Invoice not found</div>;

    // Invoice Data Preparation for Preview
    const totals = invoice.totals || {};
    const subtotal = totals.subtotal || 0;
    const cgst = totals.cgst || 0;
    const sgst = totals.sgst || 0;
    const total = totals.total || 0;

    // Round off logic (copied for consistency)
    const grossTotal = Number(subtotal) + Number(cgst) + Number(sgst);
    const roundedTotal = Math.round(grossTotal);
    const calculatedRoundOff = Math.round((roundedTotal - grossTotal) * 100) / 100;

    let roundOff = Number(totals.roundOff || 0);
    if (roundOff === 0 && Number(total) === roundedTotal && Math.abs(grossTotal - roundedTotal) > 0.001) {
        roundOff = calculatedRoundOff;
    }

    const commonProps = {
        client: invoice.client,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        placeOfSupply: invoice.placeOfSupply,
        items: invoice.items,
        subtotal: subtotal,
        cgst: cgst,
        sgst: sgst,
        roundOff: roundOff,
        total: total,
        totals: invoice.totals,
        amountInWords: invoice.amountInWords,
        printRef: printRef,
        // handlePrint passed below locally
        config: config,
        poNumber: invoice.poNumber,
        poDate: invoice.poDate,
        referenceQuoteNumber: invoice.reference,
        financialYear: invoice.financialYear,
        bankAccount: invoice.bankAccount,
        paymentTerm: invoice.paymentTerm,
        purchaseInvoiceNumber: invoice.purchaseInvoiceNumber,
        showShipTo: invoice.showShipTo
    };

    // Prepare payments for display (including legacy top-level payment if array is empty)
    const effectivePayments = (invoice.payments && invoice.payments.length > 0)
        ? invoice.payments
        : (Number(invoice.receivedAmount || 0) > 0 ? [{
            amount: invoice.receivedAmount,
            date: invoice.receivedDate || invoice.updatedAt,
            paymentMode: invoice.paymentMode || 'Bank Transfer',
            utrNumber: invoice.utrNumber || '-',
            _id: 'legacy-payment',
            isLegacy: true
        }] : []);

    const approvalStatus = invoice?.approvalStatus || 'approved'; // legacy is treated as approved
    const isApproved = invoice?.invoiceType === 'credit_note' || approvalStatus === 'approved';
    const isUnderReview = approvalStatus === 'pending_review' || approvalStatus === 'verified';

    const hasAdhocVendorDetails = Boolean(
        invoice?.department ||
        invoice?.vendorName ||
        invoice?.purchaseInvoiceNumber ||
        invoice?.vendorInvoiceDate ||
        invoice?.vendorPoNumber ||
        invoice?.vendorPoDate ||
        invoice?.purchaseInvoiceUrl
    );
    const hasAdhocDetails = Boolean(
        invoice?.isAdhocClientInvoice ||
        invoice?.invoiceType === 'adhoc' ||
        hasAdhocVendorDetails
    );
    const lastRejectEvent = (invoice?.timeline || [])
        .filter(e => e.type === 'REJECTED')
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const lastRejectReason = lastRejectEvent?.details;

    return (
        <div className="container invoice-details-container">
            <button
                className="btn invoice-back-btn"
                onClick={() => navigate('/invoice-history')}
            >
                <ArrowLeft size={16} /> Back to History
            </button>

            <Row gutter={[24, 24]}>

                {/* LEFT COLUMN: INVOICE DATA VIEW */}
                <Col xs={24} lg={18} xl={19}>
                    {/* Invoice Header Info */}
                    <Card title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>
                                <FileTextOutlined style={{ marginRight: 8 }} />
                                {invoice.invoiceType === 'credit_note' ? 'Credit Note' : 'Invoice'} #{formatInvoiceNumber(invoice.invoiceNumber, invoice.financialYear, config)}
                            </span>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <Tag
                                    color={invoice.invoiceType === 'adhoc' ? 'orange' : invoice.invoiceType === 'credit_note' ? 'purple' : 'blue'}
                                    style={{ fontWeight: 600, textTransform: 'capitalize' }}
                                >
                                    {invoice.invoiceType === 'adhoc' ? 'Adhoc' : invoice.invoiceType === 'credit_note' ? 'Credit Note' : 'Rent'}
                                </Tag>
                                {invoice.invoiceType !== 'credit_note' && !invoice.isCancelled && (
                                    <Tag
                                        color={
                                            approvalStatus === 'approved' ? 'green' :
                                            approvalStatus === 'pending_review' ? 'orange' :
                                            approvalStatus === 'verified' ? 'cyan' :
                                            approvalStatus === 'rejected' ? 'red' :
                                            'default'
                                        }
                                        style={{ fontWeight: 600, textTransform: 'capitalize' }}
                                    >
                                        {approvalStatus === 'pending_review' ? 'Pending Review' : approvalStatus}
                                    </Tag>
                                )}
                                <Tag color={(() => {
                                    if (invoice.isCancelled) return 'default';
                                    const cashReceived = Number(invoice.receivedAmount || 0);
                                    const invTotal = Number(total || 0);
                                    const currentSubtotal = Number(subtotal || 0);
                                    const totalTdsRecorded = (invoice.payments || []).reduce((sum, p) => sum + (Number(p.tdsAmount) || 0), 0);
                                    const defaultRentTds = (invoice.invoiceType === 'rent' && cashReceived === 0 && totalTdsRecorded === 0)
                                        ? (currentSubtotal * 0.10)
                                        : 0;
                                    const effectiveTds = totalTdsRecorded > 0 ? totalTdsRecorded : defaultRentTds;
                                    const netExpected = invTotal - effectiveTds;

                                    if ((cashReceived + totalTdsRecorded >= (invTotal - 1) || cashReceived >= (netExpected - 1)) && invTotal > 0) return 'green';
                                    if (cashReceived > 0 && cashReceived < (netExpected - 1)) return 'orange';
                                    if (invoice.dueDate && dayjs().isAfter(dayjs(invoice.dueDate), 'day')) return 'red';
                                    return 'blue';
                                })()}>
                                    {(() => {
                                        if (invoice.isCancelled) return 'Cancelled';
                                        const cashReceived = Number(invoice.receivedAmount || 0);
                                        const invTotal = Number(total || 0);
                                        const currentSubtotal = Number(subtotal || 0);
                                        const totalTdsRecorded = (invoice.payments || []).reduce((sum, p) => sum + (Number(p.tdsAmount) || 0), 0);
                                        const defaultRentTds = (invoice.invoiceType === 'rent' && cashReceived === 0 && totalTdsRecorded === 0)
                                            ? (currentSubtotal * 0.10)
                                            : 0;
                                        const effectiveTds = totalTdsRecorded > 0 ? totalTdsRecorded : defaultRentTds;
                                        const netExpected = invTotal - effectiveTds;

                                        if ((cashReceived + totalTdsRecorded >= (invTotal - 1) || cashReceived >= (netExpected - 1)) && invTotal > 0) return 'Paid';
                                        if (cashReceived > 0 && cashReceived < (netExpected - 1)) return 'Partially Paid';
                                        if (invoice.dueDate && dayjs().isAfter(dayjs(invoice.dueDate), 'day')) return 'Overdue';
                                        return 'Unpaid';
                                    })()}
                                </Tag>
                            </div>
                        </div>
                    } style={{ marginBottom: '20px' }}>
                        <Descriptions column={{ xs: 1, sm: 2, md: 2 }} size="small">
                            <Descriptions.Item label="Invoice Date">
                                {invoice.invoiceDate ? dayjs(invoice.invoiceDate).format('DD MMM YYYY') : 'N/A'}
                            </Descriptions.Item>
                            {invoice.invoiceType === 'credit_note' && (
                                <Descriptions.Item label="Ref Invoice No">
                                    {invoice.referenceInvoiceNumber || invoice.originalInvoice?.invoiceNumber || 'N/A'}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Due Date">
                                {invoice.dueDate ? dayjs(invoice.dueDate).format('DD MMM YYYY') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Financial Year">{invoice.financialYear || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Payment Term">{invoice.paymentTerm || 'N/A'}</Descriptions.Item>
                            {invoice.category && (
                                <Descriptions.Item label="Category">{invoice.category}</Descriptions.Item>
                            )}
                            {invoice.poNumber && (
                                <Descriptions.Item label="PO Number">{invoice.poNumber}</Descriptions.Item>
                            )}
                            {invoice.poDate && (
                                <Descriptions.Item label="PO Date">
                                    {dayjs(invoice.poDate).format('DD MMM YYYY')}
                                </Descriptions.Item>
                            )}
                            {invoice.reference && (
                                <Descriptions.Item label="Quote Reference">{invoice.reference}</Descriptions.Item>
                            )}
                            <Descriptions.Item label="Place of Supply">{invoice.placeOfSupply || 'N/A'}</Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Adhoc Details Card - Separated from main invoice details */}
                    {hasAdhocDetails && (
                        <Card
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Building size={16} style={{ color: '#2563eb' }} />
                                        <span>Adhoc Details</span>
                                    </span>
                                    {invoice.isAdhocClientInvoice ? (
                                        <Tag color="cyan">Adhoc Client Invoice</Tag>
                                    ) : (
                                        <Tag color="orange">Adhoc Invoice</Tag>
                                    )}
                                </div>
                            }
                            style={{ marginBottom: '20px' }}
                        >
                            <Descriptions column={{ xs: 1, sm: 2, md: 2 }} size="small">
                                <Descriptions.Item label="Invoice Mode">
                                    {invoice.isAdhocClientInvoice ? (
                                        <Tag color="cyan">Adhoc Client Invoice</Tag>
                                    ) : (
                                        <Tag color="orange">Adhoc Invoice</Tag>
                                    )}
                                </Descriptions.Item>
                                {invoice.department && (
                                    <Descriptions.Item label="Department">{invoice.department}</Descriptions.Item>
                                )}
                                {invoice.vendorName && (
                                    <Descriptions.Item label="Vendor Name">{invoice.vendorName}</Descriptions.Item>
                                )}
                                {invoice.purchaseInvoiceNumber && (
                                    <Descriptions.Item label="Vendor Invoice No.">{invoice.purchaseInvoiceNumber}</Descriptions.Item>
                                )}
                                {invoice.vendorInvoiceDate && (
                                    <Descriptions.Item label="Vendor Invoice Date">
                                        {dayjs(invoice.vendorInvoiceDate).format('DD MMM YYYY')}
                                    </Descriptions.Item>
                                )}
                                {invoice.vendorPoNumber && (
                                    <Descriptions.Item label="Vendor PO Number">{invoice.vendorPoNumber}</Descriptions.Item>
                                )}
                                {invoice.vendorPoDate && (
                                    <Descriptions.Item label="Vendor PO Date">
                                        {dayjs(invoice.vendorPoDate).format('DD MMM YYYY')}
                                    </Descriptions.Item>
                                )}
                                {invoice.purchaseInvoiceUrl && (
                                    <Descriptions.Item label="Vendor Invoice Doc">
                                        <a
                                            href={invoice.purchaseInvoiceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1677ff', fontWeight: 500 }}
                                        >
                                            <Paperclip size={14} /> {invoice.purchaseInvoiceOriginalName || 'View Document'}
                                        </a>
                                    </Descriptions.Item>
                                )}
                                {!hasAdhocVendorDetails && (
                                    <Descriptions.Item label="Procurement Info">
                                        <span style={{ color: '#888' }}>Direct client invoice (no vendor details linked)</span>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>
                    )}

                    {/* Bill To & Ship To */}
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        {/* Bill To Card */}
                        <Card title={<span><UserOutlined style={{ marginRight: 8 }} /> Bill To</span>} style={{ flex: 1, minWidth: '280px' }}>
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label="Name">
                                    <strong>{invoice.client?.registeredName || invoice.client?.name || 'N/A'}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Address">
                                    {invoice.client?.billToAddress || invoice.client?.address || 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="GSTIN">{invoice.client?.gstin || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Phone">{invoice.client?.phone || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                        </Card>

                        {/* Ship To Card - Only show if showShipTo is 'yes' */}
                        {invoice.showShipTo === 'yes' && (
                            <Card title={<span><EnvironmentOutlined style={{ marginRight: 8 }} /> Ship To</span>} style={{ flex: 1, minWidth: '280px' }}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Address">
                                        {invoice.client?.shipToAddress || invoice.client?.address || 'N/A'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        )}
                    </div>

                    {/* Line Items Table */}
                    <Card title={<span><BarsOutlined style={{ marginRight: 8 }} /> Line Items</span>} style={{ marginBottom: '20px' }}>
                        <Table
                            dataSource={invoice.items?.map((item, idx) => ({ ...item, key: idx }))}
                            pagination={false}
                            size="small"
                            scroll={{ x: 'max-content' }}
                            columns={invoice.invoiceType === 'adhoc' ? [
                                { title: '#', dataIndex: 'key', key: 'key', width: 50, render: (_, __, idx) => idx + 1 },
                                { title: 'HSN/SAC', dataIndex: 'hsn', key: 'hsn', width: 100 },
                                {
                                    title: 'Description',
                                    dataIndex: 'description',
                                    key: 'description',
                                    render: (text) => <div style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '50ch' }}>{text}</div>
                                },
                                { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 60, align: 'right', render: v => v || 1 },
                                { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80, align: 'right' },
                                { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 120, align: 'right', render: v => formatIndianCurrency(v || 0) },
                                { title: 'Tax %', key: 'taxPercent', width: 80, align: 'right', render: (_, record) => `${record.tax || record.taxPercent || 0}%` },
                                { title: 'Service', dataIndex: 'IsServc', key: 'IsServc', width: 80, align: 'center', render: v => v === 'Y' ? <Tag color="blue">Yes</Tag> : <Tag color="orange">No</Tag> },
                                { title: 'Amount', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, align: 'right', render: v => formatIndianCurrency(v || 0) },
                            ] : [
                                { title: '#', dataIndex: 'key', key: 'key', width: 50, render: (_, __, idx) => idx + 1 },
                                { title: 'HSN/SAC', dataIndex: 'hsn', key: 'hsn', width: 100 },
                                {
                                    title: 'Description',
                                    dataIndex: 'description',
                                    key: 'description',
                                    render: (text) => <div style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '30ch' }}>{text}</div>
                                },
                                { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, align: 'right', render: v => formatIndianCurrency(v || 0) },
                                { title: 'CGST %', key: 'cgstRate', width: 80, align: 'right', render: () => `${config.cgstRate || 0}%` },
                                { title: 'CGST Amt', key: 'cgstAmt', width: 110, align: 'right', render: (_, record) => formatIndianCurrency((Number(record.amount) || 0) * (config.cgstRate || 0) / 100) },
                                { title: 'SGST %', key: 'sgstRate', width: 80, align: 'right', render: () => `${config.sgstRate || 0}%` },
                                { title: 'SGST Amt', key: 'sgstAmt', width: 110, align: 'right', render: (_, record) => formatIndianCurrency((Number(record.amount) || 0) * (config.sgstRate || 0) / 100) },
                                {
                                    title: 'Total', key: 'total', width: 120, align: 'right', render: (_, record) => {
                                        const amt = Number(record.amount) || 0;
                                        const cgstAmt = amt * (config.cgstRate || 0) / 100;
                                        const sgstAmt = amt * (config.sgstRate || 0) / 100;
                                        return formatIndianCurrency(amt + cgstAmt + sgstAmt);
                                    }
                                },
                            ]}
                        />

                        {/* Totals Section */}
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ width: '280px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: '#666' }}>Subtotal:</span>
                                    <span>{formatIndianCurrency(subtotal)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: '#666' }}>CGST ({config.cgstRate || 0}%):</span>
                                    <span>{formatIndianCurrency(cgst)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: '#666' }}>SGST ({config.sgstRate || 0}%):</span>
                                    <span>{formatIndianCurrency(sgst)}</span>
                                </div>
                                {roundOff !== 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                        <span style={{ color: '#666' }}>Round Off:</span>
                                        <span>{formatIndianCurrency(roundOff)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 'bold', fontSize: '16px', borderTop: '2px solid #1890ff' }}>
                                    <span>Total:</span>
                                    <span style={{ color: '#1890ff' }}>{formatIndianCurrency(total)}</span>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Bank Details & Notes Section */}
                    {/* Display if either exists */}
                    {(invoice.bankAccount || invoice.notes) && (
                        <Row gutter={24} style={{ marginBottom: '24px' }}>
                            {invoice.bankAccount && (
                                <Col xs={24} md={12}>
                                    <Card
                                        title={<span><BankOutlined style={{ marginRight: 8 }} /> Bank Details</span>}
                                        className="bank-details-card"
                                        style={{ height: '100%' }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                                                <div>
                                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Bank Name</div>
                                                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#096dd9' }}>{invoice.bankAccount.bankName || 'N/A'}</div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Account Name</div>
                                                    <div style={{ fontWeight: 500, fontSize: '13px' }}>{invoice.bankAccount.accountName || 'N/A'}</div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                <div>
                                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Account Number</div>
                                                    <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px' }}>
                                                        {invoice.bankAccount.accountNumber || 'N/A'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>IFSC Code</div>
                                                    <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px' }}>
                                                        {invoice.bankAccount.ifscCode || 'N/A'}
                                                    </div>
                                                </div>
                                                <div style={{ gridColumn: 'span 2' }}>
                                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Branch</div>
                                                    <div>{invoice.bankAccount.branch || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            )}

                            {invoice.notes && (
                                <Col xs={24} md={invoice.bankAccount ? 12 : 24}>
                                    <Card
                                        title={<span><FileTextOutlined style={{ marginRight: 8 }} /> Notes & Terms</span>}
                                        style={{ height: '100%' }}
                                    >
                                        <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px', color: '#333' }}>
                                            {invoice.notes}
                                        </div>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}

                    {/* Payments Table */}
                    <Card
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span><DollarOutlined style={{ marginRight: 8 }} /> Payment Details</span>
                                {!isViewer() && invoice.invoiceType !== 'credit_note' && !invoice.isCancelled && (() => {
                                    const totalSettled = Number(invoice.receivedAmount || 0) + ((invoice.payments || []).reduce((s, p) => s + (Number(p.tdsAmount) || 0), 0) || Number(invoice.totals?.totalTds || 0));
                                    const isFullySettled = totalSettled >= (total - 1) && total > 0;
                                    const isPartiallySettled = totalSettled > 0 && !isFullySettled;
                                    return (
                                        <Tag color={isFullySettled ? 'green' : (isPartiallySettled ? 'orange' : 'default')}>
                                            {isFullySettled ? 'Settled' : (isPartiallySettled ? 'Partially Paid' : 'Pending')}
                                        </Tag>
                                    );
                                })()}
                            </div>
                        }
                        extra={
                            invoice.invoiceType !== 'credit_note' && !invoice.isCancelled && (
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<PlusCircleOutlined />}
                                    onClick={() => openPaymentModal()}
                                    disabled={!isApproved}
                                    title={!isApproved ? 'Payments cannot be recorded until this invoice is approved.' : undefined}
                                >
                                    Record Payment
                                </Button>
                            )
                        }
                        style={{ marginBottom: '20px' }}
                        className="payment-details-card"
                    >
                        {effectivePayments.length > 0 ? (
                            <>
                                <Table
                                    dataSource={effectivePayments.map((p, idx) => ({ ...p, key: p._id || idx }))}
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        {
                                            title: 'Date',
                                            dataIndex: 'date',
                                            key: 'date',
                                            render: (date) => dayjs(date).format('DD MMM YYYY')
                                        },
                                        {
                                            title: 'Method',
                                            dataIndex: 'paymentMode',
                                            key: 'paymentMode'
                                        },
                                        {
                                            title: 'Ref / UTR',
                                            dataIndex: 'utrNumber',
                                            key: 'utrNumber',
                                            render: (text) => (
                                                <span>
                                                    {text || '-'}
                                                    {effectivePayments.find(p => p.utrNumber === text)?.isLegacy && (
                                                        <Tag size="small" style={{ marginLeft: 8, fontSize: '10px' }}>Legacy</Tag>
                                                    )}
                                                </span>
                                            )
                                        },
                                        {
                                            title: 'Amount Received',
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            align: 'right',
                                            render: (val) => formatIndianCurrency(val)
                                        },
                                        {
                                            title: 'TDS Deducted',
                                            dataIndex: 'tdsAmount',
                                            key: 'tdsAmount',
                                            align: 'right',
                                            render: (val, record) => {
                                                const amt = Number(val || 0);
                                                if (amt <= 0) return <span style={{ color: '#999' }}>-</span>;
                                                return (
                                                    <div>
                                                        <span style={{ color: '#cf1322', fontWeight: 500 }}>
                                                            {formatIndianCurrency(amt)}
                                                            {record.tdsPercent ? (
                                                                <span style={{ fontSize: '11px', color: '#888', marginLeft: 4 }}>({record.tdsPercent}%)</span>
                                                            ) : null}
                                                        </span>
                                                        {record.tdsSection && (
                                                            <div style={{ marginTop: 2 }}>
                                                                <Tag color="volcano" style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px', margin: 0 }}>
                                                                    Sec {record.tdsSection}
                                                                </Tag>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Total Settled',
                                            key: 'settled',
                                            align: 'right',
                                            render: (_, record) => {
                                                const amt = Number(record.amount || 0);
                                                const tds = Number(record.tdsAmount || 0);
                                                return (
                                                    <span style={{ fontWeight: 600, color: '#1d39c4' }}>
                                                        {formatIndianCurrency(amt + tds)}
                                                    </span>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Actions',
                                            key: 'actions',
                                            align: 'center',
                                            width: 100,
                                            render: (_, record) => !isViewer() && (
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                    <Button
                                                        size="small"
                                                        type="text"
                                                        icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                                        onClick={() => openPaymentModal(record)}
                                                        disabled={invoice.isCancelled}
                                                    />
                                                    <Button
                                                        size="small"
                                                        type="text"
                                                        icon={<Trash size={14} style={{ color: '#ff4d4f' }} />}
                                                        onClick={() => handleDeletePayment(record)}
                                                        danger
                                                        disabled={invoice.isCancelled}
                                                    />
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                                <div style={{
                                    marginTop: 16,
                                    padding: '12px 16px',
                                    background: '#f8fafc',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '20px',
                                    justifyContent: 'space-between',
                                    fontSize: '13px'
                                }}>
                                    <div>
                                        <span style={{ color: '#64748b' }}>Cash Received: </span>
                                        <strong style={{ color: '#059669' }}>
                                            {formatIndianCurrency(effectivePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0))}
                                        </strong>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b' }}>TDS Deducted: </span>
                                        <strong style={{ color: '#cf1322' }}>
                                            {formatIndianCurrency(effectivePayments.reduce((s, p) => s + (Number(p.tdsAmount) || 0), 0))}
                                        </strong>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b' }}>Total Settled: </span>
                                        <strong style={{ color: '#1d39c4' }}>
                                            {formatIndianCurrency(effectivePayments.reduce((s, p) => s + (Number(p.amount) || 0) + (Number(p.tdsAmount) || 0), 0))}
                                        </strong>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b' }}>Balance Due: </span>
                                        <strong style={{
                                            color: Math.max(0, total - effectivePayments.reduce((s, p) => s + (Number(p.amount) || 0) + (Number(p.tdsAmount) || 0), 0)) < 1 ? '#059669' : '#cf1322'
                                        }}>
                                            {formatIndianCurrency(Math.max(0, total - effectivePayments.reduce((s, p) => s + (Number(p.amount) || 0) + (Number(p.tdsAmount) || 0), 0)))}
                                        </strong>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                No payments have been recorded for this invoice yet.
                            </div>
                        )}
                    </Card>

                    <Card title={<span><HistoryOutlined style={{ marginRight: 8 }} /> History</span>} style={{ marginBottom: '20px' }}>
                        <Timeline
                            mode="start"
                            items={sortedTimeline.length > 0 ? sortedTimeline.map((item, idx) => ({
                                key: idx,
                                dot: getTimelineIcon(item.type),
                                color: "gray",
                                children: getTimelineText(item)
                            })) : [
                                {
                                    dot: <PlusCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />,
                                    children: (
                                        <div>
                                            <div style={{ fontWeight: 500 }}>Created by {invoice.creator?.name || 'System'}</div>
                                            <div style={{ fontSize: '12px', color: '#888' }}>
                                                {invoice.createdAt ? dayjs(invoice.createdAt).format('DD MMM YYYY, h:mm A') : 'N/A'}
                                            </div>
                                        </div>
                                    )
                                }
                            ]}
                        />
                    </Card>

                    {/* Hidden print ref for PDF/Print functionality */}
                    <div style={{ display: 'none' }}>
                        <div ref={printRef}>
                            {invoice.invoiceType === 'adhoc' ? (
                                <AdhocInvoicePreview {...commonProps} hideInlinePrintButton={true} />
                            ) : (
                                <InvoicePreview
                                    {...commonProps}
                                    isCreditNote={invoice.invoiceType === 'credit_note'}
                                    originalInvoiceDate={invoice.originalInvoice?.invoiceDate}
                                    invoiceRefNumber={invoice.originalInvoice?.invoiceNumber}
                                />
                            )}
                        </div>
                    </div>
                </Col>

                {/* RIGHT COLUMN: ACTIONS */}
                <Col xs={24} lg={6} xl={5}>

                    {/* APPROVAL WORKFLOW CARD */}
                    {invoice.invoiceType !== 'credit_note' && !invoice.isCancelled && (
                        <Card
                            title={<span><SafetyCertificateOutlined style={{ marginRight: 8, color: '#1890ff' }} /> Approval Workflow</span>}
                            style={{ marginBottom: '16px' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <span style={{ fontSize: '13px', color: '#666' }}>Status:</span>
                                <Tag
                                    color={
                                        approvalStatus === 'approved' ? 'green' :
                                        approvalStatus === 'pending_review' ? 'orange' :
                                        approvalStatus === 'verified' ? 'cyan' :
                                        approvalStatus === 'rejected' ? 'red' :
                                        'default'
                                    }
                                    style={{ fontWeight: 600, textTransform: 'capitalize' }}
                                >
                                    {approvalStatus === 'pending_review' ? 'Pending Review' : approvalStatus}
                                </Tag>
                            </div>

                            {invoice.assignedTo && (
                                <div style={{ fontSize: '12px', color: '#555', marginBottom: 12, padding: '6px 10px', background: '#f5f5f5', borderRadius: '4px' }}>
                                    <UserOutlined style={{ marginRight: 6 }} />
                                    Reviewer: <strong>{typeof invoice.assignedTo === 'object' ? (invoice.assignedTo.name || invoice.assignedTo.email) : 'Assigned User'}</strong>
                                </div>
                            )}

                            {approvalStatus === 'rejected' && lastRejectReason && (
                                <div style={{ fontSize: '12px', color: '#cf1322', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: '4px', padding: '8px', marginBottom: 12 }}>
                                    <strong>Rejection Reason:</strong> {lastRejectReason}
                                </div>
                            )}

                            {approvalStatus === 'approved' && (
                                <div style={{ fontSize: '13px', color: '#389e0d', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                    <CheckCircleFilled style={{ color: '#52c41a' }} /> Invoice is approved.
                                </div>
                            )}

                            {!userIsViewer && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                                    {/* DRAFT */}
                                    {approvalStatus === 'draft' && (
                                        <>
                                            <Button
                                                type="primary"
                                                icon={<Send size={15} />}
                                                onClick={() => setSubmitModalOpen(true)}
                                                loading={actionLoading}
                                                block
                                            >
                                                Submit for Review
                                            </Button>
                                            {userIsAdmin && (
                                                <Button
                                                    type="primary"
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                    icon={<CheckCircle size={15} />}
                                                    onClick={handleApproveInvoice}
                                                    loading={actionLoading}
                                                    block
                                                >
                                                    Approve Invoice
                                                </Button>
                                            )}
                                            <Button
                                                icon={<UserOutlined />}
                                                onClick={() => setAssignModalOpen(true)}
                                                block
                                            >
                                                Assign Reviewer
                                            </Button>
                                        </>
                                    )}

                                    {/* PENDING REVIEW */}
                                    {approvalStatus === 'pending_review' && (
                                        <>
                                            {(!isCreator || userIsAdmin) && (
                                                <Button
                                                    type="primary"
                                                    style={{ background: '#13c2c2', borderColor: '#13c2c2' }}
                                                    icon={<Check size={15} />}
                                                    onClick={handleVerifyInvoice}
                                                    loading={actionLoading}
                                                    block
                                                >
                                                    Verify Invoice
                                                </Button>
                                            )}
                                            {userIsAdmin && (
                                                <Button
                                                    type="primary"
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                    icon={<CheckCircle size={15} />}
                                                    onClick={handleApproveInvoice}
                                                    loading={actionLoading}
                                                    block
                                                >
                                                    Approve Invoice
                                                </Button>
                                            )}
                                            {(!isCreator || userIsAdmin) && (
                                                <Button
                                                    danger
                                                    icon={<XCircle size={15} />}
                                                    onClick={() => setRejectModalOpen(true)}
                                                    loading={actionLoading}
                                                    block
                                                >
                                                    Reject / Return
                                                </Button>
                                            )}
                                            {isCreator && !userIsAdmin && (
                                                <div style={{ fontSize: '12px', color: '#fa8c16' }}>
                                                    Submitted for review. Awaiting checker verification.
                                                </div>
                                            )}
                                            <Button
                                                icon={<UserOutlined />}
                                                onClick={() => setAssignModalOpen(true)}
                                                block
                                            >
                                                Reassign Reviewer
                                            </Button>
                                        </>
                                    )}

                                    {/* VERIFIED */}
                                    {approvalStatus === 'verified' && (
                                        <>
                                            {userIsAdmin ? (
                                                <>
                                                    <Button
                                                        type="primary"
                                                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                        icon={<CheckCircle size={15} />}
                                                        onClick={handleApproveInvoice}
                                                        loading={actionLoading}
                                                        block
                                                    >
                                                        Approve Invoice
                                                    </Button>
                                                    <Button
                                                        danger
                                                        icon={<XCircle size={15} />}
                                                        onClick={() => setRejectModalOpen(true)}
                                                        loading={actionLoading}
                                                        block
                                                    >
                                                        Reject / Return
                                                    </Button>
                                                </>
                                            ) : (
                                                <div style={{ fontSize: '12px', color: '#13c2c2' }}>
                                                    Verified. Waiting for admin approval.
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* REJECTED */}
                                    {approvalStatus === 'rejected' && (
                                        <>
                                            <Button
                                                type="primary"
                                                icon={<Send size={15} />}
                                                onClick={() => setSubmitModalOpen(true)}
                                                loading={actionLoading}
                                                block
                                            >
                                                Resubmit for Review
                                            </Button>
                                            {userIsAdmin && (
                                                <Button
                                                    type="primary"
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                    icon={<CheckCircle size={15} />}
                                                    onClick={handleApproveInvoice}
                                                    loading={actionLoading}
                                                    block
                                                >
                                                    Approve Invoice
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </Card>
                    )}

                    {/* Actions Card */}
                    <Card title={<span><ThunderboltOutlined style={{ marginRight: 8 }} /> Actions</span>}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {!isViewer() && (
                                <>
                                    <Button
                                        type="primary"
                                        icon={<Edit size={16} />}
                                        onClick={openEditModal}
                                        disabled={isUnderReview}
                                        title={isUnderReview ? 'Cannot edit invoice while under review' : undefined}
                                        block
                                    >
                                        Edit
                                    </Button>

                                    {invoice.invoiceType !== 'credit_note' && (
                                        <Button
                                            icon={<Coins size={16} />}
                                            onClick={() => openPaymentModal()}
                                            disabled={invoice.isCancelled || !isApproved}
                                            title={!isApproved ? 'Invoice must be approved before receiving payments' : undefined}
                                            block
                                        >
                                            Receive Payment
                                        </Button>
                                    )}
                                </>
                            )}

                            <Button
                                icon={<Printer size={16} />}
                                onClick={handlePrint}
                                disabled={invoice.isCancelled}
                                block
                            >
                                Print
                            </Button>

                            <Button
                                icon={downloading ? <Spin size="small" /> : <Download size={16} />}
                                onClick={handleDownloadPDF}
                                disabled={downloading || invoice.isCancelled}
                                loading={downloading}
                                block
                            >
                                Download PDF
                            </Button>

                            <Button
                                icon={<FileText size={16} />}
                                onClick={handleDownloadJSON}
                                disabled={invoice.isCancelled}
                                block
                            >
                                Download GST JSON
                            </Button>

                            {!isViewer() && (
                                <>
                                    <Divider style={{ margin: '8px 0' }} />

                                    <Button
                                        icon={<XCircle size={16} />}
                                        onClick={handleCancelInvoice}
                                        disabled={invoice.isCancelled || invoice.invoiceType === 'credit_note'}
                                        block
                                        danger
                                    >
                                        {invoice.isCancelled ? 'Invoice Cancelled' : 'Cancel Invoice'}
                                    </Button>

                                    <Button
                                        icon={<Trash size={16} />}
                                        onClick={handleDeleteInvoice}
                                        block
                                        danger
                                        type="primary"
                                    >
                                        Delete Invoice
                                    </Button>
                                </>
                            )}
                        </div>
                    </Card>

                    {/* Net Expected Amount Card */}
                    {invoice.invoiceType !== 'credit_note' && (
                        <Card
                            style={{
                                marginTop: '16px',
                                background: '#e6f7ff',
                                border: '1px solid #91d5ff',
                                borderRadius: '8px'
                            }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                                <div>
                                    <div style={{ fontWeight: 600, color: '#0050b3', marginBottom: '4px' }}>
                                        Net Expected Amount
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#003a8c' }}>
                                        {(() => {
                                            const recordedTds = (invoice.payments || []).reduce((s, p) => s + (Number(p.tdsAmount) || 0), 0) || Number(invoice.totals?.totalTds || 0);
                                            if (recordedTds > 0) {
                                                return `Total amount to be received after TDS (${formatIndianCurrency(recordedTds)}) deductions from the payee:`;
                                            }
                                            if (invoice.invoiceType === 'rent') {
                                                return 'Total amount to be received after TDS (10%) deductions from the payee:';
                                            }
                                            return 'Total invoice amount to be received from the payee:';
                                        })()}
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0050b3', marginTop: '8px' }}>
                                        {(() => {
                                            const recordedTds = (invoice.payments || []).reduce((s, p) => s + (Number(p.tdsAmount) || 0), 0) || Number(invoice.totals?.totalTds || 0);
                                            if (recordedTds > 0) {
                                                return formatIndianCurrency(Math.max(0, total - recordedTds));
                                            }
                                            if (invoice.invoiceType === 'rent') {
                                                return formatIndianCurrency(Math.max(0, total - (subtotal * 0.10)));
                                            }
                                            return formatIndianCurrency(total);
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}

                </Col>
            </Row>

            {/* --- MODALS --- */}

            {/* Payment Modal */}
            <Modal
                title={<h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>{editingPayment ? 'Edit Payment' : 'Record Payment'}</h2>}
                open={paymentModalOpen}
                onCancel={() => {
                    setPaymentModalOpen(false);
                    setEditingPayment(null);
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setPaymentModalOpen(false);
                        setEditingPayment(null);
                    }}>
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSavePayment}>
                        {editingPayment ? 'Update Payment' : 'Add Payment'}
                    </Button>,
                ]}
                width={720}
                destroyOnHidden
            >
                {invoice && (
                    <div>
                        {/* Payment History Table */}
                        {invoice.payments && invoice.payments.length > 0 && (
                            <div style={{ marginBottom: 20 }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: 10 }}>Payment History</h3>
                                <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                        <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                                            <tr>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                                                <th style={{ padding: '8px', textAlign: 'right' }}>Cash Received</th>
                                                <th style={{ padding: '8px', textAlign: 'right' }}>TDS Deducted</th>
                                                <th style={{ padding: '8px', textAlign: 'right' }}>Settled</th>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Mode</th>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Ref #</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoice.payments.map((p, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px' }}>{dayjs(p.date).format('DD/MM/YY')}</td>
                                                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 500 }}>{formatIndianCurrency(p.amount)}</td>
                                                    <td style={{ padding: '8px', textAlign: 'right', color: Number(p.tdsAmount) > 0 ? '#cf1322' : '#888' }}>
                                                        {Number(p.tdsAmount) > 0 ? (
                                                            <div>
                                                                <div>{formatIndianCurrency(p.tdsAmount)}</div>
                                                                {p.tdsSection && (
                                                                    <div style={{ fontSize: '10px', color: '#888' }}>Sec {p.tdsSection}</div>
                                                                )}
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                                                        {formatIndianCurrency((Number(p.amount) || 0) + (Number(p.tdsAmount) || 0))}
                                                    </td>
                                                    <td style={{ padding: '8px' }}>{p.paymentMode}</td>
                                                    <td style={{ padding: '8px', fontSize: '11px', color: '#666' }}>{p.utrNumber || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <td style={{ padding: '8px', fontWeight: 600 }}>Total</td>
                                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                                                    {formatIndianCurrency(invoice.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0))}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#cf1322' }}>
                                                    {formatIndianCurrency(invoice.payments.reduce((s, p) => s + (Number(p.tdsAmount) || 0), 0))}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1d39c4' }}>
                                                    {formatIndianCurrency(invoice.payments.reduce((s, p) => s + (Number(p.amount) || 0) + (Number(p.tdsAmount) || 0), 0))}
                                                </td>
                                                <td colSpan={2}></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}

                        {(() => {
                            const invSubtotal = Number(invoice.totals?.subtotal || 0);
                            const invTotal = Number(invoice.totals?.total || 0);
                            const allPayments = invoice.payments || [];
                            const priorPayments = editingPayment
                                ? allPayments.filter(p => String(p._id || p.id) !== String(editingPayment._id || editingPayment.id))
                                : allPayments;
                            const priorReceived = priorPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                            const priorTds = priorPayments.reduce((s, p) => s + (Number(p.tdsAmount) || 0), 0);
                            const grossRemaining = Math.max(0, invTotal - priorReceived - priorTds);

                            const currentReceived = Number(paymentForm.receivedAmount || 0);
                            const currentTds = Number(paymentForm.tdsAmount || 0);
                            const totalSettledNow = Math.round((currentReceived + currentTds) * 100) / 100;
                            const diff = Math.round((grossRemaining - totalSettledNow) * 100) / 100;

                            return (
                                <div style={{
                                    borderTop: (invoice.payments && invoice.payments.length > 0) ? '1px solid #e2e8f0' : 'none',
                                    margin: (invoice.payments && invoice.payments.length > 0) ? '20px 0 16px' : '4px 0 16px',
                                    paddingTop: (invoice.payments && invoice.payments.length > 0) ? 16 : 0
                                }}>
                                    <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: 12 }}>
                                        {editingPayment ? 'Edit Payment Details' : 'Add New Payment'}
                                    </h3>

                                    {/* Context & Subtotal Info Banner */}
                                    <div style={{
                                        background: '#f0f5ff',
                                        border: '1px solid #adc6ff',
                                        borderRadius: '6px',
                                        padding: '12px 16px',
                                        marginBottom: 16
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '13px' }}>
                                            <span>Gross Invoice Total: <strong>{formatIndianCurrency(invTotal)}</strong></span>
                                            <span>Base Subtotal (Pre-Tax): <strong style={{ color: '#0958d9' }}>{formatIndianCurrency(invSubtotal)}</strong></span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#595959' }}>
                                            <span>Remaining to Settle: <strong>{formatIndianCurrency(grossRemaining)}</strong></span>
                                            <span><em>* TDS applies on Base Subtotal ({formatIndianCurrency(invSubtotal)})</em></span>
                                        </div>
                                    </div>

                                    {/* Quick TDS Selection Dropdown */}
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '13px' }}>
                                            Quick TDS Selection
                                        </label>
                                        <Select
                                            style={{ width: '100%' }}
                                            placeholder="Choose a quick TDS preset..."
                                            value={(() => {
                                                if (paymentForm.tdsPercent === '0') return '0';
                                                if (paymentForm.tdsPercent === '2' && paymentForm.tdsSection === '194C') return '2_194C';
                                                if (paymentForm.tdsPercent === '10' && paymentForm.tdsSection === '194-I') return '10_194I';
                                                if (paymentForm.tdsPercent === '2' && paymentForm.tdsSection === '194J') return '2_194J';
                                                if (paymentForm.tdsPercent === '10' && paymentForm.tdsSection === '194J') return '10_194J';
                                                if (paymentForm.tdsPercent === '5' && paymentForm.tdsSection === '194H') return '5_194H';
                                                if (paymentForm.tdsPercent === '1' && paymentForm.tdsSection === '194C') return '1_194C';
                                                if (paymentForm.tdsPercent) return 'custom';
                                                return undefined;
                                            })()}
                                            onChange={(val) => {
                                                if (val === '0') handleTdsRateChange(0, '');
                                                else if (val === '2_194C') handleTdsRateChange(2, '194C');
                                                else if (val === '10_194I') handleTdsRateChange(10, '194-I');
                                                else if (val === '2_194J') handleTdsRateChange(2, '194J');
                                                else if (val === '10_194J') handleTdsRateChange(10, '194J');
                                                else if (val === '5_194H') handleTdsRateChange(5, '194H');
                                                else if (val === '1_194C') handleTdsRateChange(1, '194C');
                                            }}
                                            options={[
                                                { value: '0', label: '0% (No TDS)' },
                                                { value: '2_194C', label: `2% TDS - Sec 194C (Works / Contracts) - ${formatIndianCurrency(Math.round(invSubtotal * 0.02 * 100) / 100)}` },
                                                { value: '10_194I', label: `10% TDS - Sec 194-I (Rent) - ${formatIndianCurrency(Math.round(invSubtotal * 0.10 * 100) / 100)}` },
                                                { value: '2_194J', label: `2% TDS - Sec 194J (Tech / Royalty) - ${formatIndianCurrency(Math.round(invSubtotal * 0.02 * 100) / 100)}` },
                                                { value: '10_194J', label: `10% TDS - Sec 194J (Professional Services) - ${formatIndianCurrency(Math.round(invSubtotal * 0.10 * 100) / 100)}` },
                                                { value: '5_194H', label: `5% TDS - Sec 194H (Commission / Brokerage) - ${formatIndianCurrency(Math.round(invSubtotal * 0.05 * 100) / 100)}` },
                                                { value: '1_194C', label: `1% TDS - Sec 194C (Individual Contractor) - ${formatIndianCurrency(Math.round(invSubtotal * 0.01 * 100) / 100)}` },
                                                ...(paymentForm.tdsPercent && !['0', '2', '10', '5', '1'].includes(paymentForm.tdsPercent) ? [
                                                    { value: 'custom', label: `Custom (${paymentForm.tdsPercent}%) - ${formatIndianCurrency(Number(paymentForm.tdsAmount || 0))}` }
                                                ] : [])
                                            ]}
                                        />
                                    </div>

                                    {/* TDS Section Dropdown */}
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                                            TDS Act / Section (Income Tax) {Number(paymentForm.tdsAmount) > 0 && <span style={{ color: 'red' }}>*</span>}
                                        </label>
                                        <Select
                                            showSearch
                                            allowClear
                                            style={{ width: '100%' }}
                                            placeholder="Select TDS Act Section (e.g. 194C, 194-I, 194J...)"
                                            value={paymentForm.tdsSection || undefined}
                                            onChange={handleTdsSectionChange}
                                            filterOption={(input, option) =>
                                                (option?.searchStr ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={TDS_SECTIONS.map(s => ({
                                                value: s.section,
                                                label: `Section ${s.section} – ${s.name}${s.defaultRate != null ? ` (${s.defaultRate}%)` : ''}`,
                                                searchStr: `${s.section} ${s.name}`
                                            }))}
                                        />
                                        <span style={{ fontSize: '11px', color: '#888' }}>
                                            Select the applicable Income Tax Act section under which tax was deducted
                                        </span>
                                    </div>

                                    {/* TDS % and TDS Amount row */}
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <div style={{ marginBottom: 16 }}>
                                                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                                                    TDS Percentage (%)
                                                </label>
                                                <Input
                                                    suffix="%"
                                                    value={paymentForm.tdsPercent}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(val)) {
                                                            handleTdsRateChange(val);
                                                        }
                                                    }}
                                                    placeholder="e.g. 2 or 10"
                                                />
                                                <span style={{ fontSize: '11px', color: '#888' }}>
                                                    Calculated on base ₹{invSubtotal.toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div style={{ marginBottom: 16 }}>
                                                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                                                    TDS Amount Deducted
                                                </label>
                                                <Input
                                                    prefix="₹"
                                                    value={paymentForm.tdsAmount}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(val)) {
                                                            handleTdsAmountChange(val);
                                                        }
                                                    }}
                                                    placeholder="TDS amount"
                                                />
                                                <span style={{ fontSize: '11px', color: '#888' }}>
                                                    Amount withheld by payee
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Cash Received Input with Auto-match helper */}
                                    <Row gutter={16}>
                                        <Col span={16}>
                                            <div style={{ marginBottom: 16 }}>
                                                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                                                    Received Cash (Bank Deposit) <span style={{ color: 'red' }}>*</span>
                                                </label>
                                                <Input
                                                    prefix="₹"
                                                    value={paymentForm.receivedAmount}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(val)) {
                                                            setPaymentForm(p => ({ ...p, receivedAmount: val }));
                                                        }
                                                    }}
                                                    placeholder="Enter amount deposited in bank"
                                                />
                                            </div>
                                        </Col>
                                        <Col span={8}>
                                            <div style={{ marginBottom: 16 }}>
                                                <label style={{ display: 'block', marginBottom: 6, visibility: 'hidden' }}>Auto-Fill</label>
                                                <Button
                                                    style={{ width: '100%' }}
                                                    onClick={() => {
                                                        const curTds = Number(paymentForm.tdsAmount || 0);
                                                        const neededCash = Math.max(0, Math.round((grossRemaining - curTds) * 100) / 100);
                                                        setPaymentForm(p => ({ ...p, receivedAmount: String(neededCash) }));
                                                    }}
                                                >
                                                    Auto-fill Balance
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Settlement Status / Match Card */}
                                    <div style={{
                                        padding: '10px 14px',
                                        borderRadius: '6px',
                                        marginBottom: 16,
                                        background: Math.abs(diff) < 1 ? '#f6ffed' : (diff > 0 ? '#fffbe6' : '#fff2f0'),
                                        border: `1px solid ${Math.abs(diff) < 1 ? '#b7eb8f' : (diff > 0 ? '#ffe58f' : '#ffccc7')}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{ fontSize: '13px' }}>
                                            Cash: <strong>{formatIndianCurrency(currentReceived)}</strong> + TDS: <strong style={{ color: '#cf1322' }}>{formatIndianCurrency(currentTds)}</strong> = Total Settled: <strong>{formatIndianCurrency(totalSettledNow)}</strong>
                                        </div>
                                        <div>
                                            {Math.abs(diff) < 1 ? (
                                                <Tag color="success">✓ Matches Invoice Total</Tag>
                                            ) : diff > 0 ? (
                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                    <Tag color="warning">Partial (Remaining: {formatIndianCurrency(diff)})</Tag>
                                                    {currentTds === 0 && invSubtotal > 0 && currentReceived > 0 && (
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            style={{ padding: 0, fontSize: 11 }}
                                                            onClick={() => {
                                                                handleTdsAmountChange(String(diff));
                                                            }}
                                                        >
                                                            Set remaining as TDS ({Math.round((diff / invSubtotal) * 10000) / 100}%)
                                                        </Button>
                                                    )}
                                                </div>
                                            ) : (
                                                <Tag color="error">Exceeds by {formatIndianCurrency(Math.abs(diff))}</Tag>
                                            )}
                                        </div>
                                    </div>

                                    {/* Date and Mode */}
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <div style={{ marginBottom: 16 }}>
                                                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Payment Date</label>
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    value={paymentForm.receivedDate ? dayjs(paymentForm.receivedDate) : null}
                                                    onChange={(date, dateString) => setPaymentForm(p => ({ ...p, receivedDate: dateString }))}
                                                />
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div style={{ marginBottom: 16 }}>
                                                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Payment Mode</label>
                                                <Select
                                                    style={{ width: '100%' }}
                                                    value={paymentForm.paymentMode}
                                                    onChange={val => setPaymentForm(p => ({ ...p, paymentMode: val }))}
                                                >
                                                    <Select.Option value="Bank Transfer">Bank Transfer</Select.Option>
                                                    <Select.Option value="Cheque">Cheque</Select.Option>
                                                    <Select.Option value="Cash">Cash</Select.Option>
                                                    <Select.Option value="UPI">UPI</Select.Option>
                                                    <Select.Option value="Credit Card">Credit Card</Select.Option>
                                                </Select>
                                            </div>
                                        </Col>
                                    </Row>

                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Reference # (UTR / Cheque No.)</label>
                                        <Input
                                            value={paymentForm.utrNumber}
                                            onChange={e => setPaymentForm(p => ({ ...p, utrNumber: e.target.value }))}
                                            placeholder="Enter transaction reference number"
                                        />
                                    </div>

                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Notes</label>
                                        <Input.TextArea
                                            value={paymentForm.paymentNotes}
                                            onChange={e => setPaymentForm(p => ({ ...p, paymentNotes: e.target.value }))}
                                            placeholder="Any additional notes..."
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal
                title={<div style={{ color: '#ff4d4f', fontWeight: 600 }}>Reject Invoice & Return for Rework</div>}
                open={rejectModalOpen}
                onCancel={() => {
                    setRejectModalOpen(false);
                    setRejectComment('');
                }}
                onOk={handleRejectInvoice}
                okText="Reject Invoice"
                okButtonProps={{ danger: true, loading: actionLoading }}
                confirmLoading={actionLoading}
            >
                <p style={{ color: '#666', marginBottom: 12 }}>
                    Please provide a reason for rejecting this invoice. The creator will be notified and can edit and resubmit it.
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter rejection reason (required)..."
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                />
            </Modal>

            {/* Submit for Review Modal */}
            <Modal
                title={<div style={{ fontWeight: 600 }}>Submit Invoice for Review</div>}
                open={submitModalOpen}
                onCancel={() => {
                    setSubmitModalOpen(false);
                    setSubmitComment('');
                    setSubmitAssignee(null);
                }}
                onOk={handleSubmitForReview}
                okText="Submit for Review"
                confirmLoading={actionLoading}
            >
                <p style={{ color: '#666', marginBottom: 12 }}>
                    This will lock the invoice for editing and transition it to <strong>Pending Review</strong>.
                </p>
                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: 6 }}>
                        Assign to Reviewer (Optional)
                    </label>
                    <Select
                        placeholder="Select a reviewer"
                        style={{ width: '100%' }}
                        allowClear
                        value={submitAssignee}
                        onChange={setSubmitAssignee}
                        options={users.filter(u => u.role !== 'viewer').map(u => ({
                            value: u._id || u.id,
                            label: `${u.name || u.email} (${u.role})`
                        }))}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: 6 }}>
                        Note / Comment (Optional)
                    </label>
                    <Input.TextArea
                        rows={3}
                        placeholder="Add any notes for the reviewer..."
                        value={submitComment}
                        onChange={(e) => setSubmitComment(e.target.value)}
                    />
                </div>
            </Modal>

            {/* Assign Modal */}
            <Modal
                title={<div style={{ fontWeight: 600 }}>Assign Invoice Reviewer</div>}
                open={assignModalOpen}
                onCancel={() => {
                    setAssignModalOpen(false);
                    setSelectedAssignee(null);
                }}
                onOk={handleAssignInvoice}
                okText="Assign"
                confirmLoading={actionLoading}
            >
                <p style={{ color: '#666', marginBottom: 12 }}>
                    Select an active team member to review this invoice:
                </p>
                <Select
                    placeholder="Select a user"
                    style={{ width: '100%' }}
                    value={selectedAssignee}
                    onChange={setSelectedAssignee}
                    options={users.filter(u => u.role !== 'viewer').map(u => ({
                        value: u._id || u.id,
                        label: `${u.name || u.email} (${u.role})`
                    }))}
                />
            </Modal>

            {/* Reusable Edit Invoice Modal */}
            <EditInvoiceModal
                invoice={editingInvoice}
                visible={!!editingInvoice}
                onClose={() => setEditingInvoice(null)}
                onSave={handleEditSubmit}
                clients={clients}
                config={config}
            />
        </div>
    );
}

export default InvoiceDetails;
